import React, { useEffect, useState } from 'react';
import { Sparkles, Image } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

const AIImageGeneration: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const handleTryGenerator = () => {
    if (user) {
      navigate('/design');
    } else {
      navigate('/login');
    }
  };

  const inspirationImages = [
    "https://images.pexels.com/photos/1114425/pexels-photo-1114425.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
    "https://images.pexels.com/photos/2253870/pexels-photo-2253870.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
    "https://images.pexels.com/photos/1395967/pexels-photo-1395967.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
    "https://images.pexels.com/photos/1128782/pexels-photo-1128782.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
  ];

  return (
    <section className="py-20 relative overflow-hidden">
      <div className="absolute -top-20 -left-20 w-80 h-80 rounded-full bg-brand-purple/10 blur-3xl"></div>
      
      <div className="container-custom relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          <div className="lg:w-1/2 order-2 lg:order-1">
            <div className="glass-card p-8 relative">
              <div className="grid grid-cols-2 gap-4">
                {inspirationImages.map((img, index) => (
                  <div key={index} className="aspect-square overflow-hidden rounded-lg relative group">
                    <img 
                      src={img} 
                      alt={`Wedding inspiration ${index + 1}`} 
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                      <span className="text-sm text-white">Generated with AI</span>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="glass-card mt-6 p-4 flex items-center gap-3">
                <Sparkles className="h-5 w-5 text-brand-purple" />
                <span className="text-sm text-gray-700">
                  "Enchanting rustic barn celebration with delicate lavender accents and twinkling lights"
                </span>
              </div>
            </div>
          </div>
          
          <div className="lg:w-1/2 space-y-6 order-1 lg:order-2">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 rounded-full bg-brand-purple/20 flex items-center justify-center">
                <Image className="h-5 w-5 text-brand-purple" />
              </div>
              <span className="text-brand-purple font-medium">AI-Powered Design</span>
            </div>
            
            <h2 className="text-[42px] font-bold font-cormorant-garamond leading-[1.2] text-[#FF4E8E]">
              Transform Ideas into <span className="italic text-[#FF4E8E]">Visual Magic</span>
            </h2>
            
            <p className="font-cormorant-garamond font-normal text-[16px] text-[#535353]">
              Experience the future of wedding design with our advanced AI visualization tool. Simply describe your dream wedding concept, and watch as our AI transforms your words into <span className='italic'>stunning, realistic imagery</span> that captures <span className='italic'>your vision</span> perfectly.
            </p>
            
            <ul className="space-y-3">
              {[
                'Create stunning mood boards and design concepts',
                'Explore diverse style combinations <span class=\'italic\'>instantly</span>',
                'Share visual inspiration with your vendors',
                'Refine your wedding aesthetic <span class=\'italic\'>effortlessly</span>'
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-2 text-[15px] font-cormorant-garamond font-normal text-[#535353]">
                  <span className="text-brand-purple mt-1">✓</span>
                  <span dangerouslySetInnerHTML={{__html: item}} />
                </li>
              ))}
            </ul>
            
            <button 
              onClick={handleTryGenerator}
              className="btn-primary mt-4 text-[18px] font-cormorant-garamond font-bold text-white rounded-lg"
            >
              Try AI Generator
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// CheckIcon component
const CheckIcon = ({ className }: { className?: string }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

export default AIImageGeneration;